import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, Check } from 'lucide-react';
import { api, money, pct, errMsg, card, input } from './costingApi';

const KIND = {
  below_cost: { label: 'Below cost', cls: 'bg-rose-600 text-white' },
  low_margin: { label: 'Low margin', cls: 'bg-amber-500 text-slate-900' },
  no_cost: { label: 'No cost captured', cls: 'bg-slate-600 text-white' },
};

const Row = ({ r, onReview }) => (
  <tr className="border-b border-slate-700" data-testid={`margin-alert-row-${r.sale_id}`}>
    <td className="py-2 font-mono text-xs">{r.date}</td>
    <td>{r.part}<div className="text-[11px] text-slate-400">{r.customer} · {r.salesman}</div></td>
    <td className="text-right">{money(r.total)}</td>
    <td className="text-right">{r.cost ? money(r.cost) : '—'}</td>
    <td className={`text-right font-bold ${r.profit < 0 ? 'text-rose-400' : ''}`}>{r.profit == null ? '—' : money(r.profit)}</td>
    <td className="text-right">{pct(r.margin_pct)}</td>
    <td className="pl-3"><span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${KIND[r.kind].cls}`}>{KIND[r.kind].label}</span></td>
    <td className="text-right">
      <Button size="sm" variant="outline" className="h-7 border-slate-500 bg-transparent text-white hover:bg-slate-700" onClick={() => onReview(r)} data-testid={`margin-alert-review-${r.sale_id}`}><Check className="h-3.5 w-3.5 mr-1" />Mark reviewed</Button>
    </td>
  </tr>
);

export const MarginAlerts = () => {
  const [data, setData] = useState(null);
  const [target, setTarget] = useState('');

  const load = useCallback(async () => {
    try { const d = await api.get('/margin-alerts'); setData(d); setTarget(String(d.min_margin_pct)); }
    catch (e) { toast.error(errMsg(e, 'Could not load margin alerts')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveTarget = async () => {
    try { await api.put('/margin-alerts/settings', { min_margin_pct: Number(target) }); toast.success('Target margin saved'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const review = async (r) => {
    const note = window.prompt(`Mark "${r.part}" as reviewed. Optional note:`, '');
    if (note === null) return;
    try { await api.post(`/margin-alerts/${r.sale_id}/review`, { note }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const c = data?.counts || {};
  return (
    <Card className={card} data-testid="margin-alerts">
      <CardHeader className="pb-2 flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-400" /> Low-Margin Sales (last 90 days)</CardTitle>
        <div className="flex items-center gap-2 text-sm">
          Target margin
          <Input type="number" min="0" max="95" value={target} onChange={(e) => setTarget(e.target.value)} className={`w-20 h-8 ${input}`} data-testid="margin-target-input" />%
          <Button size="sm" onClick={saveTarget} className="h-8 bg-amber-500 hover:bg-amber-400 text-slate-900" data-testid="margin-target-save">Save</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-4 text-sm" data-testid="margin-alert-counts">
          <span><b className="text-rose-400">{c.below_cost || 0}</b> below cost ({money(Math.abs(data?.below_cost_loss || 0))} lost)</span>
          <span><b className="text-amber-300">{c.low_margin || 0}</b> under {data?.min_margin_pct ?? '—'}%</span>
          <span><b className="text-slate-300">{c.no_cost || 0}</b> with no cost captured</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-slate-400 border-b border-slate-600">
              <th className="text-left py-2">Date</th><th className="text-left">Part</th><th className="text-right">Sold for</th><th className="text-right">Cost</th><th className="text-right">Profit</th><th className="text-right">Margin</th><th className="text-left pl-3">Flag</th><th />
            </tr></thead>
            <tbody>{(data?.rows || []).map((r) => <Row key={r.sale_id} r={r} onReview={review} />)}</tbody>
          </table>
          {data && data.rows.length === 0 && <p className="text-center text-slate-400 py-6 text-sm">No low-margin sales. Every sale meets the {data.min_margin_pct}% target.</p>}
        </div>
      </CardContent>
    </Card>
  );
};
